import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Pencil, Trash2, Send, KeyRound, User, Check, X, MessageCircle,
  ShieldAlert, ChevronLeft, ChevronRight, Lock,
} from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import MealPrefsCell from "../reception/MealPrefsCell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";import BookingAccessDialog from "@/components/BookingAccessDialog";
import { BookingDetailDialog } from "../CalendarTab";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FilterTab } from "./helpers";
import SelfCheckinLink from "./SelfCheckinLink";
import OtaContactButtons from "./OtaContactButtons";
import OtaBookingDetailModal from "./OtaBookingDetailModal";
import DeleteBookingButton from "./DeleteBookingButton";

export default function BookingsTab({ property, rooms }) {
  const { t, lang } = useLang();
  const IT = lang === "it";
  const [bs, setBs] = useState([]);
  // Subscription tier drives the "Ultime prenotazioni" lock. Pro/Enterprise
  // → unlocked (paid feature). Base/expired → grey overlay + padlock.
  const [subTier, setSubTier] = useState("base");
  const [subStatus, setSubStatus] = useState("");
  useEffect(() => {
    api.get("/subscriptions/status")
      .then((r) => { setSubTier(r.data?.tier || "base"); setSubStatus(r.data?.status || ""); })
      .catch(() => {});
  }, []);
  const latestUnlocked = (subTier === "pro" || subTier === "enterprise") && (subStatus === "active" || subStatus === "trialing");
  const [editBookingId, setEditBookingId] = useState(null);
  const [otaDetail, setOtaDetail] = useState(null);
  const [filter, setFilter] = useState("today");
  const _todayLocal = (() => {
    const dt = new Date();
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  })();
  const [dateFilter, setDateFilter] = useState(_todayLocal);
  // "Ultime prenotazioni" section — separate list below the main table,
  // filtered by BOOKING DATE (created_at). Default is today. The host can
  // scrub back/forward day by day to audit intake.
  const [latestDate, setLatestDate] = useState(_todayLocal);
  const [search, setSearch] = useState("");
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const load = () => api.get("/bookings/me", { params: { include_ota: 1 } }).then((r) => setBs(r.data || []));
  useEffect(() => { load(); }, []);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkKind, setBulkKind] = useState("checkin_done");
  const [bulkBusy, setBulkBusy] = useState("");

  useEffect(() => { setPage(1); setSelectedIds(new Set()); }, [filter, search, bs.length]);
  useEffect(() => { setSelectedIds(new Set()); }, [page]);

  const deepBookingId = searchParams.get("booking") || "";
  useEffect(() => {
    if (!deepBookingId || !bs.length) return;
    const match = bs.find((x) => x.id === deepBookingId);
    if (!match) return;
    setFilter("all");
    if (match.is_ota) setOtaDetail(match); else setEditBookingId(match.id);
    const next = new URLSearchParams(searchParams);
    next.delete("booking");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepBookingId, bs.length]);

  const _localIso = (dt) => {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };
  const todayIso = _localIso(new Date());
  const yesterdayIso = _localIso(new Date(Date.now() - 86400000));
  const tomorrowIso = _localIso(new Date(Date.now() + 86400000));
  const in7DaysIso = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const isInHouseToday = (b) => {
    const ci = (b.check_in || "").slice(0, 10);
    const co = (b.check_out || "").slice(0, 10);
    return ci && co && ci <= todayIso && todayIso < co;
  };
  // "Booking date" — when the reservation was created on our side. For direct
  // bookings it's the ``created_at`` we persist at checkout / manual add. For
  // OTA rows the backend already picks ``imported_at`` as best proxy so we
  // just read a single field here.
  const _bookingDateIso = (b) => {
    const raw = b.created_at || b.booked_at || null;
    if (!raw) return "";
    // Handle both ISO datetime strings ("2026-02-25T14:30:00Z") and bare dates.
    return String(raw).slice(0, 10);
  };
  const sorted = [...bs].sort((a, b) => (a.check_in || "").localeCompare(b.check_in || ""));
  const searchTrim = search.trim().toLowerCase();
  const matchesSearch = (b) => {
    if (!searchTrim) return true;
    if ((b.guest_name || "").toLowerCase().includes(searchTrim)) return true;
    if ((b.guest_email || "").toLowerCase().includes(searchTrim)) return true;
    if ((b.guest_phone || "").toLowerCase().includes(searchTrim)) return true;
    if ((b.external_ref || "").toLowerCase().includes(searchTrim)) return true;
    if ((b.id || "").toLowerCase().includes(searchTrim)) return true;
    return false;
  };
  const searched = sorted.filter(matchesSearch);
  const filtered = searched.filter((b) => {
    const ci = (b.check_in || "").slice(0, 10);
    if (filter === "date" && dateFilter) return ci === dateFilter;
    if (filter === "all") return true;
    if (filter === "today") return ci === todayIso || isInHouseToday(b);
    if (!ci) return false;
    if (filter === "yesterday") return ci === yesterdayIso;
    if (filter === "tomorrow") return ci === tomorrowIso;
    if (filter === "upcoming7") return ci >= todayIso && ci <= in7DaysIso;
    return true;
  });
  const counts = {
    all: searched.length,
    yesterday: searched.filter(b => (b.check_in || "").slice(0, 10) === yesterdayIso).length,
    today: searched.filter(b => (b.check_in || "").slice(0, 10) === todayIso || isInHouseToday(b)).length,
    tomorrow: searched.filter(b => (b.check_in || "").slice(0, 10) === tomorrowIso).length,
    upcoming7: searched.filter(b => {
      const ci = (b.check_in || "").slice(0, 10);
      return ci >= todayIso && ci <= in7DaysIso;
    }).length,
  };
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const paged = filtered.slice(pageStart, pageStart + PAGE_SIZE);
  const otaProviderLabel = (src) => {
    const s = (src || "").toLowerCase();
    if (s.includes("booking")) return { label: "Booking.com", color: "#003580" };
    if (s.includes("airbnb")) return { label: "Airbnb", color: "#FF5A5F" };
    if (s.includes("expedia")) return { label: "Expedia", color: "#F7B801" };
    if (s.includes("vrbo")) return { label: "Vrbo", color: "#2E86DE" };
    return { label: src || "OTA", color: "#666666" };
  };
  const onAccessSaved = (bookingId, newOverride) => {
    setBs((prev) => prev.map((b) => b.id === bookingId ? { ...b, access_override: newOverride } : b));
  };
  const chargeNoShow = async (b) => {
    const amt = (b.no_show_penalty_amount ?? b.amount)?.toFixed(2);
    const kind = (b.no_show_penalty_kind || "full").toLowerCase();
    const kindLabels = {
      full: IT ? "100% del totale" : "100% of total",
      "30_percent": IT ? "30% del totale" : "30% of total",
      first_night: IT ? "prima notte" : "first night",
      free_5d: IT ? "cancellazione gratuita fino a 5 giorni prima (scaduta)" : "free cancellation until 5 days before (expired)",
      free_3d: IT ? "cancellazione gratuita fino a 3 giorni prima (scaduta)" : "free cancellation until 3 days before (expired)",
      free_1d: IT ? "cancellazione gratuita fino a 1 giorno prima (scaduta)" : "free cancellation until 1 day before (expired)",
    };
    const kindTxt = kindLabels[kind] || kindLabels.full;
    if (!window.confirm(IT
        ? `Confermi l'addebito no-show di € ${amt} (${kindTxt}) sulla carta a garanzia di ${b.guest_name}?\nQuesta azione è irreversibile.`
        : `Confirm no-show charge of € ${amt} (${kindTxt}) on ${b.guest_name}'s guarantee card?\nThis is irreversible.`)) return;
    try {
      const r = await api.post(`/bookings/${b.id}/charge-no-show`);
      toast.success(IT ? `Addebitato € ${r.data.amount.toFixed(2)}` : `Charged € ${r.data.amount.toFixed(2)}`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Error"));
    }
  };
  const markPaid = async (b) => {
    if (!window.confirm(IT
      ? `Confermi che la prenotazione di ${b.guest_name} è stata pagata? Verrà emesso subito il documento fiscale.`
      : `Confirm ${b.guest_name}'s booking has been paid? The fiscal document will be issued immediately.`)) return;
    try {
      await api.post(`/bookings/${b.id}/mark-paid`, {});
      toast.success(IT ? "Prenotazione segnata come pagata" : "Booking marked as paid");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Error"));
    }
  };

  const toggleRow = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAllOnPage = (checked, rows) => setSelectedIds((prev) => {
    const next = new Set(prev);
    for (const r of rows) {
      if (checked) next.add(r.id); else next.delete(r.id);
    }
    return next;
  });
  const runBulk = async (channel) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(channel);
    try {
      const r = await api.post("/bookings/bulk-enqueue-messages", {
        booking_ids: ids, template_kind: bulkKind, channels: [channel],
      });
      const s = r.data.stats || {};
      const parts = [];
      if (channel === "whatsapp") {
        parts.push(IT ? `${s.enqueued_whatsapp || 0} in coda` : `${s.enqueued_whatsapp || 0} queued`);
        if (s.skipped_no_phone) parts.push(IT ? `${s.skipped_no_phone} senza telefono` : `${s.skipped_no_phone} no phone`);
      } else {
        parts.push(IT ? `${s.sent_email || 0} email inviate` : `${s.sent_email || 0} emails sent`);
        if (s.skipped_no_email) parts.push(IT ? `${s.skipped_no_email} senza email` : `${s.skipped_no_email} no email`);
        if (s.email_errors) parts.push(IT ? `${s.email_errors} errori` : `${s.email_errors} errors`);
      }
      toast.success(parts.join(" · "));
      setSelectedIds(new Set());
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBulkBusy(""); }
  };

  const manualPill = (b) => {
    const isManualOverride = !!(b?.manual_overrides && b.manual_overrides.payment_status);
    if (!isManualOverride) return null;
    return (
      <span
        className="ml-1 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-sage/15 text-sage border border-sage/40 align-middle"
        title={IT ? "Stato impostato manualmente dall'host" : "Manually set by the host"}
        data-testid={`payment-manual-badge-${b.id}`}
      >
        <Pencil className="w-2.5 h-2.5"/>{IT ? "Manuale" : "Manual"}
      </span>
    );
  };

  const statusPill = (b) => {
    const ps = b.payment_status || "";
    const pill = (() => {
      if (ps === "paid") return <span className="text-xs px-2 py-1 rounded-full bg-sage text-white">paid</span>;
      if (ps === "pending") return <span className="text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-700 border border-amber-500/40 whitespace-nowrap inline-block" title={IT ? "Pagamento non ancora ricevuto" : "Payment not yet received"}>{IT ? "Da incassare" : "To collect"}</span>;
      if (ps === "guaranteed") return <span className="text-xs px-2 py-1 rounded-full bg-primary/15 text-primary border border-primary/30" title={IT ? "Carta a garanzia" : "Card on file"}>{IT ? "Garantito" : "Guaranteed"}{b.card_last4 ? ` · •••• ${b.card_last4}` : ""}</span>;
      if (ps === "guarantee_pending") return <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">{IT ? "Attesa carta" : "Awaiting card"}</span>;
      if (ps === "no_show_charged") return <span className="text-xs px-2 py-1 rounded-full bg-destructive text-white" title={IT ? "Penale addebitata" : "Penalty charged"}>{IT ? "No-show ✗" : "No-show ✗"}</span>;
      if (ps === "refunded") return <span className="text-xs px-2 py-1 rounded-full bg-blue-500/15 text-blue-700 border border-blue-500/40" title={IT ? "Prenotazione rimborsata" : "Booking refunded"}>{IT ? "Rimborsato" : "Refunded"}</span>;
      if (ps === "ota") {
        const meta = otaProviderLabel(b.source);
        return <span className="text-xs px-2 py-1 rounded-full text-white font-semibold whitespace-nowrap" style={{ background: meta.color }} title={IT ? `Prenotazione da ${meta.label}` : `Booking from ${meta.label}`}>{meta.label}</span>;
      }
      return <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">{ps}</span>;
    })();
    const arrivedPill = b.reception_status === "arrived" ? (
      <span
        className="text-[10px] px-2 py-0.5 rounded-full bg-sage text-white font-semibold uppercase tracking-widest"
        title={IT ? `Arrivato${b.checked_in_at ? ` il ${new Date(b.checked_in_at).toLocaleString('it-IT')}` : ""}` : `Arrived${b.checked_in_at ? ` on ${new Date(b.checked_in_at).toLocaleString('en-US')}` : ""}`}
        data-testid={`booking-arrived-pill-${b.id}`}
      >
        ✓ {IT ? "Arrivato" : "Arrived"}
      </span>
    ) : null;
    const departedPill = b.departure_status === "departed" ? (
      <span
        className="text-[10px] px-2 py-0.5 rounded-full bg-primary text-primary-foreground font-semibold uppercase tracking-widest"
        title={IT ? `Partito${b.checked_out_at ? ` il ${new Date(b.checked_out_at).toLocaleString('it-IT')}` : ""}` : `Departed${b.checked_out_at ? ` on ${new Date(b.checked_out_at).toLocaleString('en-US')}` : ""}`}
        data-testid={`booking-departed-pill-${b.id}`}
      >
        ✓ {IT ? "Partito" : "Departed"}
      </span>
    ) : null;
    return <span className="inline-flex items-center flex-wrap gap-1">{pill}{manualPill(b)}{arrivedPill}{departedPill}</span>;
  };
  return (
    <div className="space-y-4">
      <div className="relative max-w-md" data-testid="bookings-search-wrap">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={IT ? "Cerca ospite per nome, email o telefono…" : "Search guest by name, email or phone…"}
          className="w-full pl-9 pr-9 py-2 rounded-md border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          data-testid="bookings-search-input"
          aria-label={IT ? "Cerca prenotazioni" : "Search bookings"}
        />
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
        </svg>
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            data-testid="bookings-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 inline-flex items-center justify-center"
            aria-label={IT ? "Cancella ricerca" : "Clear search"}
          >
            <X className="w-3.5 h-3.5"/>
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="bookings-filters">
        <FilterTab id="yesterday" label={IT ? "Ieri" : "Yesterday"} count={counts.yesterday} active={filter === "yesterday"} onClick={() => { setDateFilter(""); setFilter("yesterday"); }}/>
        <FilterTab id="today" label={IT ? "Oggi" : "Today"} count={counts.today} active={filter === "today"} onClick={() => { setDateFilter(""); setFilter("today"); }}/>
        <FilterTab id="tomorrow" label={IT ? "Domani" : "Tomorrow"} count={counts.tomorrow} active={filter === "tomorrow"} onClick={() => { setDateFilter(""); setFilter("tomorrow"); }}/>
        <FilterTab id="upcoming7" label={IT ? "Prossimi 7 giorni" : "Next 7 days"} count={counts.upcoming7} active={filter === "upcoming7"} onClick={() => { setDateFilter(""); setFilter("upcoming7"); }}/>
        <FilterTab id="all" label={IT ? "Tutte" : "All"} count={counts.all} active={filter === "all"} onClick={() => { setDateFilter(""); setFilter("all"); }}/>
        <div className="ml-auto flex items-end gap-2 pl-1.5 border-l border-border/60">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setDateFilter((cur) => {
                const base = cur || todayIso;
                const d = new Date(base + "T00:00:00");
                d.setDate(d.getDate() - 1);
                return _localIso(d);
              });
              setFilter("date");
            }}
            data-testid="bookings-date-prev"
          >←</Button>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {IT ? "Data" : "Date"}
            </Label>
            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => { setDateFilter(e.target.value); if (e.target.value) setFilter("date"); }}
              className="h-9 w-40"
              data-testid="bookings-date-filter"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setDateFilter((cur) => {
                const base = cur || todayIso;
                const d = new Date(base + "T00:00:00");
                d.setDate(d.getDate() + 1);
                return _localIso(d);
              });
              setFilter("date");
            }}
            data-testid="bookings-date-next"
          >→</Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setDateFilter(todayIso); setFilter("date"); }}
            data-testid="bookings-date-today"
          >
            {IT ? "Oggi" : "Today"}
          </Button>
        </div>
        <div className="text-xs text-muted-foreground italic hidden sm:block basis-full order-last text-right pt-1">
          {IT ? "Ordinate per data di arrivo · include OTA" : "Sorted by arrival · includes OTA"}
        </div>
      </div>
      {selectedIds.size > 0 && (
        <div className="sticky top-2 z-20 flex flex-wrap items-center gap-3 border border-primary/40 bg-primary/10 backdrop-blur-md rounded-lg px-4 py-3" data-testid="bookings-bulk-bar">
          <div className="text-sm font-semibold text-primary">
            {IT ? `${selectedIds.size} selezionate` : `${selectedIds.size} selected`}
          </div>
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Template" : "Template"}</label>
            <select
              value={bulkKind}
              onChange={(e) => setBulkKind(e.target.value)}
              data-testid="bookings-bulk-template"
              className="text-xs px-2 py-1 rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="new_booking">{IT ? "Nuova prenotazione" : "New booking"}</option>
              <option value="checkin_done">{IT ? "Check-in completato" : "Check-in done"}</option>
              <option value="before_checkout">{IT ? "Pomeriggio pre check-out" : "Before check-out"}</option>
              <option value="after_checkout">{IT ? "24h dopo check-out" : "24h after check-out"}</option>
            </select>
          </div>
          <button
            type="button"
            onClick={() => runBulk("whatsapp")}
            disabled={!!bulkBusy}
            data-testid="bookings-bulk-whatsapp"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#25D366] text-white hover:bg-[#128C7E] transition-all font-semibold disabled:opacity-50"
          >
            <MessageCircle className="w-3.5 h-3.5"/>
            {bulkBusy === "whatsapp" ? "…" : (IT ? "Aggiungi a coda WhatsApp" : "Queue WhatsApp")}
          </button>
          <button
            type="button"
            onClick={() => runBulk("email")}
            disabled={!!bulkBusy}
            data-testid="bookings-bulk-email"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-all font-semibold disabled:opacity-50"
          >
            <Send className="w-3.5 h-3.5"/>
            {bulkBusy === "email" ? "…" : (IT ? "Invia Email a tutti" : "Send Email to all")}
          </button>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            data-testid="bookings-bulk-clear"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-border text-muted-foreground hover:border-destructive hover:text-destructive transition-all"
          >
            <X className="w-3.5 h-3.5"/>
            {IT ? "Deseleziona" : "Clear"}
          </button>
        </div>
      )}
      {!filtered.length ? (
        <div className="text-sm text-muted-foreground text-center py-12 border border-dashed border-border rounded-lg" data-testid="bookings-empty">
          {filter === "all"
            ? t("dash.no_bookings")
            : (IT ? "Nessuna prenotazione in questo intervallo." : "No bookings in this range.")}
        </div>
      ) : (
      <div className="overflow-x-auto border border-border rounded-lg bg-card" data-testid="bookings-table">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 uppercase tracking-[0.15em] text-[10px]">
          <tr>
            <th className="p-3 w-8">
              <input
                type="checkbox"
                aria-label={IT ? "Seleziona tutte in questa pagina" : "Select all on this page"}
                data-testid="bookings-select-all"
                checked={paged.length > 0 && paged.every((r) => selectedIds.has(r.id))}
                ref={(el) => { if (el) el.indeterminate = paged.some((r) => selectedIds.has(r.id)) && !paged.every((r) => selectedIds.has(r.id)); }}
                onChange={(e) => toggleAllOnPage(e.target.checked, paged)}
              />
            </th>
            <th className="text-left p-3">{IT ? "Ospite" : "Guest"}</th><th className="text-left p-3">{IT ? "Camera" : "Room"}</th>
            <th className="text-left p-3">Check-in</th><th className="text-left p-3">Check-out</th>
            <th className="text-left p-3 whitespace-nowrap">{IT ? "Ora arrivo" : "Arrival"}</th>
            <th className="text-left p-3 whitespace-nowrap" title={IT ? "Colazione + note alimentari (uso interno)" : "Breakfast + dietary (internal)"}>
              {IT ? "Colazione" : "Meals"}
            </th>
            <th className="text-right p-3">{IT ? "Costo totale" : "Total cost"}</th><th className="text-left p-3">{IT ? "Stato" : "Status"}</th>
            <th className="text-left p-3">{IT ? "Accesso" : "Access"}</th>
            <th className="text-left p-3 sticky right-0 bg-muted/50 backdrop-blur-sm shadow-[inset_1px_0_0_0_hsl(var(--border))]">{IT ? "Azioni" : "Actions"}</th>
          </tr>
        </thead>
        <tbody>
          {paged.map((b) => {
            const isOta = !!b.is_ota;
            const eff = b.access_override?.type || property?.access_instructions?.type || "in_person";
            const isCustom = !!b.access_override?.type;
            const canChargeNoShow = !isOta && b.payment_method === "at_property"
              && b.payment_status === "guaranteed";
            const nights = (() => {
              try {
                const ms = new Date(b.check_out) - new Date(b.check_in);
                const n = Math.round(ms / 86400000);
                return n > 0 ? n : null;
              } catch { return null; }
            })();
            const openDetail = () => { if (isOta) setOtaDetail(b); else setEditBookingId(b.id); };
            const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
            const isSelected = selectedIds.has(b.id);
            return (
              <tr
                key={b.id}
                className={`border-t border-border cursor-pointer hover:bg-primary/5 transition-colors ${isOta ? "bg-muted/10" : ""} ${isSelected ? "!bg-primary/10" : ""}`}
                onClick={openDetail}
                data-testid={`bookings-row-${b.id}`}
                title={IT ? "Clicca per vedere i dettagli completi" : "Click to see full details"}
              >
                <td className="p-3 w-8" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleRow(b.id)}
                    data-testid={`bookings-select-${b.id}`}
                    aria-label={IT ? `Seleziona ${b.guest_name}` : `Select ${b.guest_name}`}
                  />
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium">{b.guest_name}</span>
                    {isOta && (
                      <span className="text-[9px] uppercase tracking-widest text-white font-semibold rounded px-1.5 py-0.5" style={{ background: otaProviderLabel(b.source).color }} title={IT ? "Prenotazione importata da OTA" : "Booking imported from OTA"}>
                        {otaProviderLabel(b.source).label}
                      </span>
                    )}
                    {b.guests != null && b.guests > 0 && (
                      <span
                        className="text-[9px] uppercase tracking-widest text-muted-foreground bg-muted rounded-full px-1.5 py-0.5 inline-flex items-center gap-0.5"
                        title={IT ? `${b.guests} ${b.guests === 1 ? "ospite" : "ospiti"}` : `${b.guests} ${b.guests === 1 ? "guest" : "guests"}`}
                        data-testid={`booking-guests-badge-${b.id}`}
                      >
                        <User className="w-2.5 h-2.5"/>{b.guests}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {b.guest_email || (isOta && b.external_ref ? `Rif: ${b.external_ref}` : "")}
                  </div>
                </td>
                <td className="p-3">{b.room_name}</td>
                <td className="p-3">{b.check_in}</td>
                <td className="p-3">{b.check_out}</td>
                <td className="p-3 whitespace-nowrap" data-testid={`booking-arrival-${b.id}`}>
                  {b.arrival_time ? (
                    <span className="font-mono text-sm">{b.arrival_time}</span>
                  ) : b.guest_eta ? (
                    <span className="text-xs text-muted-foreground italic" title={IT ? "ETA libero comunicato dall'ospite" : "Free-text ETA sent by guest"}>
                      {String(b.guest_eta).slice(0, 24)}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="p-3" onClick={(e) => e.stopPropagation()}>
                  <MealPrefsCell
                    booking={b}
                    IT={IT}
                    onSaved={(id, patch) => setBs((prev) => prev.map((x) => x.id === id ? { ...x, ...patch } : x))}
                  />
                </td>
                <td className="p-3 text-right">
                  {b.amount != null ? (
                    <div className="flex flex-col items-end">
                      <span className="font-display text-lg text-primary leading-tight">€ {Number(b.amount).toFixed(2)}</span>
                      {nights && (
                        <span className="text-[10px] text-muted-foreground mt-0.5">
                          {nights} {IT ? (nights === 1 ? "notte" : "notti") : (nights === 1 ? "night" : "nights")}
                          {b.amount > 0 && ` · € ${(Number(b.amount) / nights).toFixed(0)}/${IT ? "notte" : "night"}`}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—{nights ? ` · ${nights} ${IT ? "notti" : "nights"}` : ""}</span>
                  )}
                </td>
                <td className="p-3">{statusPill(b)}</td>
                <td className="p-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2">
                    <BookingAccessDialog
                      booking={b}
                      propertyDefault={property?.access_instructions}
                      onSaved={(ov) => onAccessSaved(b.id, ov)}
                      trigger={
                        <button
                          type="button"
                          className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border transition-all lift ${isCustom ? "border-primary bg-primary/10 text-primary font-medium" : "border-border bg-card text-muted-foreground hover:border-primary/40"}`}
                          data-testid={`booking-access-trigger-${b.id}`}
                          title={IT ? "Configura tipo check-in per questa prenotazione" : "Set check-in type for this booking"}
                        >
                          {eff === "self" ? <KeyRound className="w-3 h-3"/> : <User className="w-3 h-3"/>}
                          {eff === "self" ? "Self" : (IT ? "Persona" : "In-person")}
                          {isCustom && <span className="text-[9px] uppercase tracking-widest">·{IT ? "Custom" : "Custom"}</span>}
                        </button>
                      }
                    />
                    <SelfCheckinLink booking={b} eff={eff} IT={IT}/>
                  </div>
                </td>
                <td className="p-3 sticky right-0 bg-card shadow-[inset_1px_0_0_0_hsl(var(--border))] whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={stop(() => { if (isOta) setOtaDetail(b); else setEditBookingId(b.id); })}
                    data-testid={isOta ? `ota-detail-btn-${b.id}` : `edit-booking-btn-${b.id}`}
                    className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border border-primary/50 bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-all lift mr-1 whitespace-nowrap font-medium"
                    title={IT ? "Modifica prenotazione" : "Edit booking"}
                  >
                    <Pencil className="w-3 h-3"/>
                    {IT ? "Modifica" : "Edit"}
                  </button>
                  <OtaContactButtons booking={b} IT={IT}/>
                  {!isOta && b.payment_status === "pending" && (
                    <button
                      type="button"
                      onClick={stop(() => markPaid(b))}
                      data-testid={`mark-paid-btn-${b.id}`}
                      className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border border-sage/50 bg-sage/10 text-sage hover:bg-sage hover:text-white transition-all lift ml-1 whitespace-nowrap"
                      title={IT ? "Segna come pagata (emette il documento fiscale)" : "Mark as paid (issues the fiscal document)"}
                    >
                      <Check className="w-3 h-3"/>
                      {IT ? "Segna pagata" : "Mark paid"}
                    </button>
                  )}
                  {canChargeNoShow && (
                    <button
                      type="button"
                      onClick={stop(() => chargeNoShow(b))}
                      data-testid={`no-show-btn-${b.id}`}
                      className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border border-destructive/40 bg-destructive/5 text-destructive hover:bg-destructive hover:text-white transition-all lift ml-1"
                      title={IT ? "Addebita penale no-show" : "Charge no-show penalty"}
                    >
                      <ShieldAlert className="w-3 h-3"/>
                      {IT ? "Segna no-show" : "Mark no-show"}
                    </button>
                  )}
                  <DeleteBookingButton booking={b} onDeleted={load} IT={IT}/>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <BookingDetailDialog
        bookingId={editBookingId}
        rooms={rooms || []}
        onClose={() => setEditBookingId(null)}
        onChanged={() => { setEditBookingId(null); load(); }}
      />
      </div>
      )}
      {filtered.length > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground mt-3" data-testid="bookings-pagination">
          <div>
            {IT
              ? `Prenotazioni ${pageStart + 1}–${Math.min(pageStart + PAGE_SIZE, filtered.length)} di ${filtered.length}`
              : `Bookings ${pageStart + 1}–${Math.min(pageStart + PAGE_SIZE, filtered.length)} of ${filtered.length}`}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              data-testid="bookings-page-prev"
              className="w-8 h-8 rounded-md border border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-primary transition-all disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center"
              aria-label={IT ? "Pagina precedente" : "Previous page"}
            >
              <ChevronLeft className="w-4 h-4"/>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setPage(n)}
                data-testid={`bookings-page-${n}`}
                className={`min-w-[32px] h-8 px-2 rounded-md border text-xs font-medium transition-all ${n === currentPage ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/40 hover:text-primary"}`}
                aria-current={n === currentPage ? "page" : undefined}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              data-testid="bookings-page-next"
              className="w-8 h-8 rounded-md border border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-primary transition-all disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center"
              aria-label={IT ? "Pagina successiva" : "Next page"}
            >
              <ChevronRight className="w-4 h-4"/>
            </button>
          </div>
        </div>
      )}
      <OtaBookingDetailModal
        item={otaDetail}
        onClose={() => setOtaDetail(null)}
        otaProviderLabel={otaProviderLabel}
        IT={IT}
        rooms={rooms || []}
        onSaved={() => load()}
      />

      {/* --------------------------------------------------------------- */}
      {/* Second section: Ultime prenotazioni (by booking date)           */}
      {/* Separate list, own date picker, defaulting to today.            */}
      {/* --------------------------------------------------------------- */}
      <LatestBookingsSection
        bs={bs}
        latestDate={latestDate}
        setLatestDate={setLatestDate}
        _localIso={_localIso}
        _bookingDateIso={_bookingDateIso}
        todayIso={todayIso}
        otaProviderLabel={otaProviderLabel}
        openDetail={(b) => { if (b.is_ota) setOtaDetail(b); else setEditBookingId(b.id); }}
        unlocked={latestUnlocked}
        subTier={subTier}
        IT={IT}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// LatestBookingsSection — sibling of the main list. Filters ``bs`` by
// ``created_at`` (day granularity) so the host can see what came in on a given
// day regardless of when the guest is actually arriving. Defaults to today.
// -----------------------------------------------------------------------------
function LatestBookingsSection({ bs, latestDate, setLatestDate, _localIso, _bookingDateIso, todayIso, otaProviderLabel, openDetail, unlocked, subTier, IT }) {
  // Feature-gated: only Pro/Enterprise plans see live data. Rows are further
  // filtered to EXCLUDE iCal feeds — this section is meant for manual/direct
  // bookings + Channel API connections, our differentiator for paid tiers.
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const rows = (bs || []).filter(
    (b) => _bookingDateIso(b) === latestDate && b.ota_kind !== "ical"
  );
  rows.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
  const shift = (delta) => {
    const d = new Date((latestDate || todayIso) + "T00:00:00");
    d.setDate(d.getDate() + delta);
    setLatestDate(_localIso(d));
  };
  if (!unlocked) {
    return (
      <>
      <section className="mt-10 border-t border-border pt-8 relative" data-testid="latest-bookings-section" data-locked="true">
        <button
          type="button"
          onClick={() => setUpgradeOpen(true)}
          className="w-full text-left border border-border rounded-lg bg-muted/40 p-8 hover:bg-muted/60 hover:border-primary/40 cursor-pointer transition-all group"
          data-testid="latest-locked-panel"
          aria-label={IT ? "Sblocca Ultime prenotazioni" : "Unlock Latest bookings"}
        >
          <div className="text-center">
            <div className="inline-flex w-14 h-14 rounded-full bg-muted text-muted-foreground items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-all">
              <Lock className="w-6 h-6"/>
            </div>
            <h3 className="font-display text-2xl mb-2 text-muted-foreground group-hover:text-foreground transition-colors">
              {IT ? "Ultime prenotazioni" : "Latest bookings"}
            </h3>
            <p className="text-sm text-muted-foreground max-w-xl mx-auto mb-4 leading-relaxed">
              {IT
                ? "Sezione esclusiva dei piani Pro e Enterprise. Riceve solo prenotazioni dirette e via API dei canali collegati — nessun feed iCal — così hai una vista pulita degli ordini diretti quotidiani."
                : "Exclusive to Pro & Enterprise plans. Only direct bookings and connected Channel-API feeds flow here — no iCal noise — for a clean daily view of your direct intake."}
            </p>
            <div className="flex flex-wrap gap-2 justify-center items-center">
              <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-muted text-muted-foreground">
                {IT ? `Piano attuale: ${subTier}` : `Current plan: ${subTier}`}
              </span>
              <span className="text-sm px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold group-hover:bg-primary/90 transition-all lift">
                {IT ? "Scopri i vantaggi" : "See benefits"}
              </span>
            </div>
          </div>
        </button>
      </section>
      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent className="max-w-lg" data-testid="latest-upgrade-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                <Lock className="w-5 h-5"/>
              </div>
              <DialogTitle className="font-display text-2xl">
                {IT ? "Monitoraggio in tempo reale" : "Real-time monitoring"}
              </DialogTitle>
            </div>
            <DialogDescription>
              {IT
                ? "La sezione 'Ultime prenotazioni' è pensata per gli host che vivono le prenotazioni come si vivono gli ordini di un negozio: minuto per minuto."
                : "'Latest bookings' is built for hosts who read reservations like a shop reads orders — minute by minute."}
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-3 my-2" data-testid="latest-upgrade-benefits">
            {[
              [IT ? "Vista pulita" : "Clean view",
               IT ? "Solo prenotazioni dirette e API canali — nessun rumore iCal, mai." : "Direct bookings + Channel API only — never any iCal noise."],
              [IT ? "Badge 'Nuova' istantaneo" : "Instant 'New' badge",
               IT ? "Ogni ordine ricevuto nelle ultime 2 ore è evidenziato in tempo reale." : "Every order received in the last 2 hours is highlighted live."],
              [IT ? "Ordinamento cronologico" : "Chronological order",
               IT ? "Le più recenti in cima, con orario preciso — reagisci in secondi." : "Newest on top with exact timestamp — respond in seconds."],
              [IT ? "Filtro rapido per data" : "Fast date filter",
               IT ? "Audit dell'intake giornaliero con un click, oggi e nei giorni scorsi." : "One-click audit of daily intake, today and past days."],
            ].map(([t, d]) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-1 w-5 h-5 rounded-full bg-sage text-white flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3"/>
                </span>
                <div>
                  <div className="font-semibold text-sm">{t}</div>
                  <div className="text-xs text-muted-foreground leading-snug">{d}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-2 pt-2 border-t border-border mt-3">
            <button
              type="button"
              onClick={() => setUpgradeOpen(false)}
              className="text-sm px-4 py-2 rounded-md border border-border text-muted-foreground hover:border-destructive hover:text-destructive transition-all"
              data-testid="latest-upgrade-cancel"
            >
              {IT ? "Chiudi" : "Close"}
            </button>
            <Link
              to="/pricing"
              className="text-sm px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-all lift font-semibold inline-flex items-center gap-1"
              data-testid="latest-upgrade-cta"
              onClick={() => setUpgradeOpen(false)}
            >
              {IT ? "Vai al listino piani" : "See plans & pricing"}
              <ChevronRight className="w-4 h-4"/>
            </Link>
          </div>
        </DialogContent>
      </Dialog>
      </>
    );
  }
  return (
    <section className="mt-10 border-t border-border pt-8" data-testid="latest-bookings-section" data-locked="false">
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl leading-tight" data-testid="latest-bookings-title">
            {IT ? "Ultime prenotazioni" : "Latest bookings"}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            {IT ? "Solo prenotazioni dirette e API canali — feed iCal esclusi. Cambia data per vedere altri giorni." : "Direct bookings + Channel API only — iCal feeds excluded. Change the date to see other days."}
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => shift(-1)}
            data-testid="latest-date-prev"
            aria-label="prev"
          >←</Button>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {IT ? "Data prenotazione" : "Booking date"}
            </Label>
            <Input
              type="date"
              value={latestDate}
              onChange={(e) => setLatestDate(e.target.value)}
              className="h-9 w-40"
              data-testid="latest-date-picker"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => shift(1)}
            data-testid="latest-date-next"
            aria-label="next"
          >→</Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLatestDate(todayIso)}
            data-testid="latest-date-today"
          >
            {IT ? "Oggi" : "Today"}
          </Button>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-10 border border-dashed border-border rounded-lg" data-testid="latest-empty">
          {IT ? "Nessuna prenotazione ricevuta in questa data." : "No bookings received on this date."}
        </div>
      ) : (
        <div className="overflow-x-auto border border-border rounded-lg bg-card" data-testid="latest-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 uppercase tracking-[0.15em] text-[10px]">
              <tr>
                <th className="text-left p-3">{IT ? "Ora ordine" : "Order time"}</th>
                <th className="text-left p-3">{IT ? "Ospite" : "Guest"}</th>
                <th className="text-left p-3">{IT ? "Camera" : "Room"}</th>
                <th className="text-left p-3">Check-in</th>
                <th className="text-left p-3">Check-out</th>
                <th className="text-left p-3 whitespace-nowrap">{IT ? "Ora arrivo" : "Arrival"}</th>
                <th className="text-right p-3">{IT ? "Importo" : "Amount"}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => {
                const created = b.created_at || "";
                const t = created.length > 10 ? new Date(created).toLocaleTimeString(IT ? "it-IT" : "en-GB", { hour: "2-digit", minute: "2-digit" }) : "";
                // "Nuova" badge — prenotazione ricevuta nelle ultime 2 ore.
                const isFresh = (() => {
                  if (!created) return false;
                  const ts = Date.parse(created);
                  if (Number.isNaN(ts)) return false;
                  return (Date.now() - ts) < 2 * 3600 * 1000;
                })();
                const isOta = !!b.is_ota;
                return (
                  <tr
                    key={`latest-${b.id}`}
                    className="border-t border-border cursor-pointer hover:bg-primary/5 transition-colors"
                    onClick={() => openDetail(b)}
                    data-testid={`latest-row-${b.id}`}
                    title={IT ? "Apri dettagli" : "Open details"}
                  >
                    <td className="p-3 whitespace-nowrap text-xs text-muted-foreground">{t || "—"}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-medium">{b.guest_name || "—"}</span>
                        {isOta && (
                          <span className="text-[9px] uppercase tracking-widest text-white font-semibold rounded px-1.5 py-0.5" style={{ background: otaProviderLabel(b.source).color }}>
                            {otaProviderLabel(b.source).label}
                          </span>
                        )}
                        {isFresh && (
                          <span
                            className="text-[9px] uppercase tracking-widest font-bold text-white bg-primary rounded-full px-2 py-0.5 shadow-sm animate-pulse"
                            title={IT ? "Prenotazione arrivata nelle ultime 2 ore" : "Received in the last 2 hours"}
                            data-testid={`latest-new-badge-${b.id}`}
                          >
                            {IT ? "Nuova" : "New"}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{b.guest_email || (isOta && b.external_ref ? `Rif: ${b.external_ref}` : "")}</div>
                    </td>
                    <td className="p-3">{b.room_name || "—"}</td>
                    <td className="p-3 whitespace-nowrap">{b.check_in || "—"}</td>
                    <td className="p-3 whitespace-nowrap">{b.check_out || "—"}</td>
                    <td className="p-3 whitespace-nowrap" data-testid={`latest-arrival-${b.id}`}>
                      {b.arrival_time ? (
                        <span className="font-mono text-sm">{b.arrival_time}</span>
                      ) : b.guest_eta ? (
                        <span className="text-xs text-muted-foreground italic">{String(b.guest_eta).slice(0, 24)}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      {b.amount != null ? <span className="font-display text-primary">€ {Number(b.amount).toFixed(2)}</span> : <span className="text-muted-foreground text-xs">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="p-3 text-[11px] text-muted-foreground border-t border-border">
            {IT ? `${rows.length} ${rows.length === 1 ? "prenotazione" : "prenotazioni"} ricevute il ${latestDate}` : `${rows.length} ${rows.length === 1 ? "booking" : "bookings"} received on ${latestDate}`}
          </div>
        </div>
      )}
    </section>
  );
}
